import type { ItemFieldRow } from './item-field-reader';
import type { InventoryReadContext } from './inventory-read-context';
import { isThinnedInventoryRead } from './inventory-read-context';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readOverrideEntryAbilityId } from './layer-cell-readers';
import { permanentStatusAllowed } from './permanent-status-guard';
import { readCreatureStatuses, AuraOwnerGateUnavailable, ownerGatedAuraLivePayload } from './creature-status-reader';

export interface LayerEntriesDependencies {
    nodeItemId: (row: ItemFieldRow) => string | null;
    providers: (row: ItemFieldRow) => Iterable<ItemFieldRow>;
    inventoryContext: InventoryReadContext;
    /** Exact loaded ItemLayerTypeCatalog::aggregationStrategy result. */
    aggregationStrategy: (name: string) => string;
    abilityTypes: readonly string[];
    /** Optional tracing/instrumentation hook; defaults to the pure PHP-compatible identity reader. */
    overrideEntryAbilityId?: (entry: unknown) => string;
}
function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function has(value: unknown, key: string): boolean { return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key); }
function own(value: unknown, key: string): unknown { return has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined; }
function path(value: unknown, ...keys: string[]): unknown { for (const key of keys) value = own(value, key); return value; }
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function normalize(value: unknown): ItemFieldRow {
    if (value == null || value === '' || (arrayLike(value) && Object.keys(value).length === 0)) return [];
    return arrayLike(value) && has(value, '0') ? value : [value];
}
const defenseSlots: Readonly<Record<string, string>> = {
    damage_type_resistance: 'resistance', damage_type_immunity: 'immune', damage_type_vulnerability: 'vulnerable',
    immune_status_effect: 'immune_status', contesting_roll_adv_vs_status_effect: 'contesting_roll_adv_vs_status_effect',
    contesting_roll_dis_vs_status_effect: 'contesting_roll_dis_vs_status_effect',
};
function raw(row: ItemFieldRow, name: string): unknown {
    if (has(own(row, 'item_layer_types'), name)) return path(row, 'item_layer_types', name);
    const defense = own(defenseSlots, name) ?? (name === 'force_move_resistance' || name === 'immune_to_forced_movement' ? name : null);
    if (typeof defense === 'string') {
        const value = path(row, 'computed', 'creature', 'defenses', defense);
        if (arrayLike(value) && Object.keys(value).length) return value;
    }
    return path(row, 'computed', 'creature', 'modifiers', name) ?? own(row, name) ?? null;
}

/** PHP layer_entries control flow, including associative-list preservation on early returns.
 * Provider enumeration and catalog strategy are explicit input boundaries.
 * Pass the same inventory context to every reader in a compiler request.
 */
export function createLayerEntriesReader(dependencies: LayerEntriesDependencies): {
    read: (row: ItemFieldRow | null, name: string, visited?: ItemFieldRow) => ItemFieldRow;
    /** Immutable view for compiler consumers. Entries/providers are borrowed; never edit them. No cross-mutation cache. */
    readBorrowed: (row: ItemFieldRow | null, name: string, visited?: ItemFieldRow) => ItemFieldRow;
} {
    const abilityTypes = new Set(dependencies.abilityTypes);
    const overrideIdentity = dependencies.overrideEntryAbilityId ?? readOverrideEntryAbilityId;
    function suppress(row: ItemFieldRow, name: string, entries: ItemFieldRow): ItemFieldRow {
        if (!Object.keys(entries).length) return entries;
        if (name === 'status_effect_permanent') {
            const kept = Object.values(entries).filter(entry => permanentStatusAllowed(readLayerListEntryUuid(entry)));
            return kept.length === Object.keys(entries).length ? entries : kept;
        }
        const suppressed = path(row, 'computed', 'creature', 'suppressed_ability_ids');
        if (!arrayLike(suppressed) || !Object.keys(suppressed).length || !abilityTypes.has(name)) return entries;
        return Object.values(entries).filter(entry => {
            const id = overrideIdentity(entry);
            return id === '' || own(suppressed, id) == null;
        });
    }
    function read(row: ItemFieldRow | null, name: string, prior: ReadonlySet<string>): ItemFieldRow {
        if (row === null) return [];
        if (abilityTypes.has(name) || has(defenseSlots, name) || name === 'force_move_resistance' || name === 'immune_to_forced_movement') {
            // R-116 part 2 (2026-09-30): only an owner-gated aura whose live state can move THIS read refuses (it grants, has a foreign
            // origin, or carries the slot itself); a skill-lines-only aura (Martyr Eternal) is re-derived by the C1 compile
            for (const status of Object.values(readCreatureStatuses(row))) {
                if (ownerGatedAuraLivePayload(status, name)) throw new AuraOwnerGateUnavailable('aura-owner-live-payload-rebuild');
            }
        }
        const visited = new Set(prior), self = dependencies.nodeItemId(row);
        if (self !== null) visited.add(self);
        const abilities = path(row, 'computed', 'creature', 'abilities', name);
        if (arrayLike(abilities)) return suppress(row, name, abilities);
        if (name === 'spells_known' || name === 'curses_known') {
            const known = path(row, 'computed', 'creature', name);
            if (arrayLike(known)) return known;
        }
        const host = normalize(raw(row, name));
        if (!Object.keys(host).length && isThinnedInventoryRead(row)) {
            const answer = dependencies.inventoryContext.read(row, full => read(full, name, visited));
            if (answer !== null) return answer;
        }
        let strategy = dependencies.aggregationStrategy(name);
        const defense = has(defenseSlots, name);
        if (defense) strategy = 'append';
        if (strategy === 'host_wins') {
            if (Object.keys(host).length) return suppress(row, name, host);
            for (const carrier of dependencies.providers(row)) {
                const id = dependencies.nodeItemId(carrier);
                if (id !== null && visited.has(id)) continue;
                const entries = read(carrier, name, visited);
                // Test before host suppression: an emptied first carrier is still terminal.
                if (Object.keys(entries).length) return suppress(row, name, entries);
            }
            return [];
        }
        const seen = new Set<string>(), result: unknown[] = [];
        const accept = (entry: unknown, prepend: boolean): void => {
            const damage = own(entry, 'damage_type');
            const id = defense && typeof damage === 'string' ? damage : readLayerListEntryUuid(entry);
            if (id !== '' && seen.has(id)) return;
            if (id !== '') seen.add(id);
            if (prepend) result.unshift(entry); else result.push(entry);
        };
        for (const entry of Object.values(host)) accept(entry, false);
        for (const carrier of dependencies.providers(row)) {
            const id = dependencies.nodeItemId(carrier);
            if (id !== null && visited.has(id)) continue;
            for (const entry of Object.values(read(carrier, name, visited))) accept(entry, strategy === 'prepend');
        }
        return suppress(row, name, result);
    }
    const readBorrowed = (row: ItemFieldRow | null, name: string, visited: ItemFieldRow = {}): ItemFieldRow => {
        const entries = read(row, name, new Set(Object.entries(visited).filter(([, value]) => value != null).map(([key]) => key)));
        // Preserve PHP list/associative return shapes without walking their rich children.
        if (Array.isArray(entries)) return entries;
        const pairs = Object.entries(entries);
        return pairs.every(([key], index) => key === String(index)) ? pairs.map(([, child]) => child) : entries;
    };
    return { readBorrowed, read: (row, name, visited) => clone(readBorrowed(row, name, visited)) as ItemFieldRow };
}
